import { useMemo, useState } from 'react';
import { api } from '../../api/client';
import { useSaveFlow } from '../../components/confirm';
import { useModalKeys } from '../../hooks/useModalKeys';
import { useQueryClient } from '@tanstack/react-query';
import type { NotifyApi } from '../../components/notifications';
import '../accounting/accounting.css';
import { CityAreasPanel } from '../settings/systemManagement/CityAreasPanel';
import {
  useCities, useCityStates, useSaveCity, errMessage,
  type CityRow, type StagedArea,
} from '../settings/systemManagement/city';

// ─────────────────────────────────────────────────────────────────────────────
// GoLive "City" create/edit popup, opened from the City lookup's New/Edit (✎)
// icon on the Contact / Client address forms. GoLive's popup is City* / State
// PLUS the nested **Area** grid (New · Link · Unlink · Edit · Export to), so it
// reuses the Settings ▸ System Management <CityAreasPanel> verbatim rather than
// re-implementing that child collection (standing rule 8).
//
// z-index bump is scoped to `.srn-city-host` so it stacks above the Contact /
// Client / State modals; confirm dialogs (z 9500, portaled) stay on top.
// ─────────────────────────────────────────────────────────────────────────────

/** State dropdown label — the State's long name, falling back to its code. */
function stateLabel(s: { shortName: string; longName: string | null }): string {
  return (s.longName ?? '').trim() || s.shortName;
}

export function CityDetailModal({ cityId, stateId, onClose, onSaved, notify }: {
  cityId: string | null;                 // null = create new; else edit that city
  stateId: string | null;                // pre-selected State for a new city
  onClose: () => void;
  onSaved: (savedCityId: string) => void;
  notify: NotifyApi;
}) {
  const runSave = useSaveFlow();
  const save = useSaveCity();
  const qc = useQueryClient();
  const { data: cities = [] } = useCities();
  const { data: states = [] } = useCityStates();
  const existing = useMemo<CityRow | null>(
    () => (cityId ? cities.find((c) => c.id === cityId) ?? null : null),
    [cityId, cities]);

  const [cityName, setCityName] = useState(existing?.cityName ?? '');
  const [stId, setStId] = useState(existing?.stateId ?? stateId ?? '');
  const [err, setErr] = useState('');
  const [stagedAreas, setStagedAreas] = useState<StagedArea[]>([]);
  const [maxed, setMaxed] = useState(false);
  // Bespoke popup keyboard (standing rule 14): Esc closes this hand-rolled overlay.
  const modalKeys = useModalKeys(true, { onClose });

  // Save the city (create/update), commit any Areas staged on a brand-new city,
  // then hand the saved id back to the caller.
  const doSave = async () => {
    if (!cityName.trim()) { setErr('City must not be empty.'); return; }
    setErr('');
    const wasNew = !existing?.id;
    let newId: string | undefined;
    await runSave({
      save: async () => {
        const res = await save.mutateAsync({
          id: existing?.id, cityName: cityName.trim(), stateId: stId || null,
        });
        newId = existing?.id ?? (res as { data?: { data?: { id?: string } } })?.data?.data?.id;
        if (wasNew && newId && stagedAreas.length) {
          for (const a of stagedAreas) {
            if (a.areaId) await api.post(`/api/v1/cities/${newId}/areas/link`, { areaId: a.areaId });
            else await api.post(`/api/v1/cities/${newId}/areas`, { areaName: a.name });
          }
          await qc.invalidateQueries({ queryKey: ['sm-city-areas'] });
        }
      },
      successMessage: 'Saved successfully.',
      onSaved: () => { if (newId) onSaved(newId); else onClose(); },
      onError: (e) => notify.error(errMessage(e)),
    });
  };

  return (
    <div className="srn-city-host">
      <style>{`
        .srn-city-host .acc-overlay { z-index: 3300; }
        .srn-city-host .acc-lksel-backdrop { z-index: 3400; }
        .srn-city-host .acc-lksel-pop { z-index: 3401; }
      `}</style>
      <div className="acc-overlay" onMouseDown={onClose}>
        <div className={`acc-dialog acc-dialog-md${maxed ? ' acc-dialog-max' : ''}`} onMouseDown={(e) => e.stopPropagation()} {...modalKeys}>
          <div className="acc-dialog-hdr">
            <span>{existing ? `City - ${existing.cityName ?? ''}` : 'City'}</span>
            <span className="acc-dialog-acts">
              <i className={`ti ${maxed ? 'ti-minimize' : 'ti-maximize'} acc-dialog-x`} title={maxed ? 'Restore' : 'Maximize'} onClick={() => setMaxed((m) => !m)} />
              <i className="ti ti-x acc-dialog-x" onClick={onClose} />
            </span>
          </div>
          <div className="acc-dialog-body">
            <div className="acc-field-h">
              <label className="acc-label acc-label-req">City<span className="req">*</span></label>
              <div className="acc-fh-control">
                <input className="acc-input" autoFocus value={cityName}
                  onChange={(e) => { setCityName(e.target.value); if (err) setErr(''); }}
                  onKeyDown={(e) => { if (e.key === 'Enter') void doSave(); }} />
                {err && <div className="acc-err">{err}</div>}
              </div>
            </div>
            <div className="acc-field-h">
              <label className="acc-label">State</label>
              <div className="acc-fh-control">
                <select className="acc-input" value={stId} onChange={(e) => setStId(e.target.value)}>
                  <option value="">N/A</option>
                  {states.map((s) => <option key={s.id} value={s.id}>{stateLabel(s)}</option>)}
                </select>
              </div>
            </div>
            {/* The nested City → Area editor — GoLive parity. */}
            <CityAreasPanel cityId={existing?.id} staged={stagedAreas} onStaged={setStagedAreas} />
          </div>
          <div className="acc-dialog-ftr">
            <button className="ca-btn ca-btn-solid" onClick={() => void doSave()} disabled={save.isPending}><span className="tb-i"><i className="ti ti-device-floppy" /></span> OK</button>
            <button className="ca-btn" onClick={onClose}><span className="tb-i tb-i-grey"><i className="ti ti-arrow-back-up" /></span> Cancel</button>
          </div>
        </div>
      </div>
    </div>
  );
}
